import {JS_HEAP_SAMPLE_INTERVAL_MS} from './constants';
import type {PerfWindowHeapSampleMetrics} from './types';

const BYTES_PER_MEBIBYTE = 1024 * 1024;

/** Running summary of used JS heap readings, in bytes. */
export interface HeapSamples {
  count: number;
  min: number;
  max: number;
  last: number;
  maxIncrease: number;
}

/** Reads performance.memory.usedJSHeapSize; undefined outside Chromium. */
export function readUsedJsHeapBytes(): number | undefined {
  const used = (
    performance as Performance & {memory?: {usedJSHeapSize?: number}}
  ).memory?.usedJSHeapSize;
  return used != null && Number.isFinite(used) && used >= 0 ? used : undefined;
}

export function startHeapSamples(bytes: number): HeapSamples {
  return {count: 1, min: bytes, max: bytes, last: bytes, maxIncrease: 0};
}

export function addHeapSample(samples: HeapSamples, bytes: number): void {
  // Measure the rise from the lowest earlier reading before this one can
  // become the new low. A drop never counts as an increase.
  samples.maxIncrease = Math.max(samples.maxIncrease, bytes - samples.min);
  samples.min = Math.min(samples.min, bytes);
  samples.max = Math.max(samples.max, bytes);
  samples.last = bytes;
  samples.count++;
}

export function getHeapSampleMetrics(
  samples: HeapSamples
): PerfWindowHeapSampleMetrics {
  return {
    jsHeapEndMiB: toMiB(samples.last),
    jsHeapMaxMiB: toMiB(samples.max),
    jsHeapMinMiB: toMiB(samples.min),
    jsHeapMaxIncreaseMiB: toMiB(samples.maxIncrease),
    jsHeapSampleIntervalMs: JS_HEAP_SAMPLE_INTERVAL_MS,
    jsHeapSampleCount: samples.count,
  };
}

function toMiB(bytes: number): number {
  return Math.round(bytes / BYTES_PER_MEBIBYTE);
}
